import React, { useState, useEffect, useMemo } from 'react';
import { string, bool, any } from 'prop-types';
import { useProductDeprecated } from '@thd-nucleus/data-sources/react/dataModel/migration';
import { useStore } from '@thd-nucleus/experience-context';
import { useLayoutGroupProps } from '@thd-olt-component-react/layout-manager';
import { PromoModelProvider } from '../contexts/PromoModelProvider';
import { PromoCartProvider } from '../contexts/PromoCartProvider';
import { PromoPresentationProvider } from '../contexts/PromoPresentationProvider';
import { PromoDrawer } from './promo-drawer/PromoDrawer';
import { PromoCallToAction } from './promo-call-to-action/PromoCallToAction';
import { isValidPromotionData } from '../utils/promo-model-utils';
import { dataModel } from './dataModel';

import './PromotionProducts.scss';

const returnProductIfValid = (data) => (
    data?.product?.pricing?.promotion
    && isValidPromotionData(data.product.pricing)
      ? data.product
      : null
);

const isSanitaryAnchorItemId = (itemId) => (
  typeof itemId === 'string'
    && itemId.length === 9
    && !Number.isNaN(Number(itemId))
);

const displayPromotion = (bogoEnabled, msbEnabled, bmsmEnabled, anchorItem) => {
  const experienceTag = anchorItem?.pricing?.promotion?.experienceTag;
  return (bogoEnabled && experienceTag === 'BOGO')
      || (msbEnabled && experienceTag === 'MSBONLINE')
      || (bmsmEnabled && experienceTag === 'BMSM');
};

export const PromotionProducts = ({
  itemId, productDataModel, ...props
}) => {

  const {
    bogoEnabled, msbEnabled, bmsmEnabled
  } = useLayoutGroupProps({ props });

  useEffect(() => {
    if (bogoEnabled || msbEnabled || bmsmEnabled) {
      LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('promotion-products.ready');
    }
  }, [bogoEnabled, msbEnabled, bmsmEnabled]);

  const { storeId, storeZip } = useStore();
  const [renderPromoDrawer, setRenderPromoDrawer] = useState(false);
  const [isBatchLoading, setIsBatchLoading] = useState(true);
  // This is only used to communicate between Cart and Model Provider for use in components pull from Cart Provider
  const [currentPromoTier, setCurrentPromoTier] = useState(0);

  const { data } = useProductDeprecated({
    itemId,
    storeId,
    domains: ['$ProductPod'],
    ssr: false,
    skip: (!bogoEnabled && !msbEnabled && !bmsmEnabled) || !isSanitaryAnchorItemId(itemId),
  }, 'clientOnlyProduct');

  const anchorItem = useMemo(() => returnProductIfValid(data), [data]);

  const renderDrawer = () => setRenderPromoDrawer(true);

  return displayPromotion(bogoEnabled, msbEnabled, bmsmEnabled, anchorItem) ? (
    <PromoModelProvider
      anchorItem={anchorItem}
      storeId={storeId}
      storeZip={storeZip}
      setIsBatchLoading={setIsBatchLoading}
      currentPromoTier={currentPromoTier}
      productDataModel={productDataModel}
    >
      <PromoCartProvider setCurrentPromoTier={setCurrentPromoTier}>
        <PromoPresentationProvider isBatchLoading={isBatchLoading} renderPromoDrawer={renderPromoDrawer}>
          <PromoCallToAction
            onPromoCtaHover={renderDrawer}
            isInPromoDrawer={false}
          />
          {renderPromoDrawer && (
            <PromoDrawer />
          )}
        </PromoPresentationProvider>
      </PromoCartProvider>
    </PromoModelProvider>
  ) : null;
};

PromotionProducts.displayName = 'PromotionProducts';

PromotionProducts.propTypes = {
  itemId: string,
  /* eslint-disable-next-line react/no-unused-prop-types */
  bogoEnabled: bool,
  /* eslint-disable-next-line react/no-unused-prop-types */
  msbEnabled: bool,
  /* eslint-disable-next-line react/no-unused-prop-types */
  bmsmEnabled: bool,
  // eslint-disable-next-line react/forbid-prop-types
  productDataModel: any,
};

PromotionProducts.defaultProps = {
  itemId: '',
  bogoEnabled: true,
  msbEnabled: true,
  bmsmEnabled: false,
  productDataModel: {},
};

PromotionProducts.dataModel = dataModel;
